import React, { useState, useEffect } from 'react';
import { trackEvent } from '../analytics/GoogleAnalytics';
import { Helmet } from 'react-helmet';
import AuthorCredit from '../ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../ui/FreshnessDate';
import {
  DIGITAL_LINKS,
  MTD_PHASES,
  POSITIONING,
  formatUkLongDate,
  getMandationForQualifyingIncome,
} from '../../compliance/mtdFacts';

const MandationCalculator = ({ isEmbedded: _isEmbedded = false }) => {
  const [tradingIncome, setTradingIncome] = useState(0);
  const [propertyIncome, setPropertyIncome] = useState(0);

  useEffect(() => {
    if (tradingIncome > 0 || propertyIncome > 0) {
      const timer = setTimeout(() => {
        trackEvent('tool_completion', { tool_name: 'mtd_mandation_checker' });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [tradingIncome, propertyIncome]);

  const calculateMandation = (trading, property) => {
    const { phase, alreadyStarted } = getMandationForQualifyingIncome(trading + property);

    if (!phase) {
      return {
        phase: 'Exempt',
        date: 'N/A',
        status: 'Voluntary',
        message: 'You are below the current mandation thresholds.',
        theme: 'emerald'
      };
    }

    const startLabel = formatUkLongDate(phase.startDate);
    if (phase.phase === 1) {
      return {
        phase: phase.label,
        date: startLabel,
        status: alreadyStarted ? 'Already in scope' : 'Phase 1',
        message: alreadyStarted
          ? 'You are already required to keep digital records in compatible software for 2026/27.'
          : `Mandation starts ${startLabel}.`,
        theme: 'red'
      };
    }
    if (phase.phase === 2) {
      return {
        phase: phase.label,
        date: startLabel,
        status: alreadyStarted ? 'Already in scope' : 'Mandatory next year',
        message: alreadyStarted
          ? 'You are already required to keep digital records in compatible software.'
          : `Mandation starts ${startLabel}.`,
        theme: 'amber'
      };
    }
    return {
      phase: phase.label,
      date: startLabel,
      status: alreadyStarted ? 'Already in scope' : 'Mandatory in 2028',
      message: alreadyStarted
        ? 'You are already required to keep digital records in compatible software.'
        : `Mandation starts ${startLabel}.`,
      theme: 'indigo'
    };
  };

  const result = calculateMandation(tradingIncome, propertyIncome);
  const phase1 = MTD_PHASES[0];

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to check MTD for Income Tax mandation",
    "dateModified": getFreshnessDateIso(),
    "step": [
      { "@type": "HowToStep", "text": `Determine your mandation start date using HMRC's qualifying-income thresholds, starting with over £${phase1.thresholdGBP.toLocaleString('en-GB')}.` },
      { "@type": "HowToStep", "text": "Keep digital records in compatible software. Typing original entries into that software is allowed." },
      { "@type": "HowToStep", "text": "Capture receipts via WhatsApp or email if that helps you collect source documents, then export structured records for your accountant or filing software." }
    ]
  };

  return (
    <div className="w-full max-w-2xl mx-auto my-12">
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl">
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>

        <FreshnessDate className="mb-4" />
        <div className="mb-6">
          <h3 className="text-3xl font-extrabold text-white mb-4">MTD Mandation Checker</h3>

          <AuthorCredit />

          <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
            <p className="text-xl font-medium leading-relaxed">
              <strong>You are mandated for Making Tax Digital for Income Tax if your combined self-employment and property turnover (gross income) exceeds £{phase1.thresholdGBP.toLocaleString('en-GB')}. Use the calculator below to check your MTD start date.</strong>
            </p>
          </div>

          <div className="prose prose-invert max-w-none text-slate-300 mb-8">
            <p>
              This threshold applies to your absolute gross revenue, not your net profit. If you were over this amount in the 2024/25 tax year, you must use <strong>MTD ITSA</strong> compatible software.
              {DIGITAL_LINKS.summary} {POSITIONING.noSubmit}
            </p>
          </div>

          <p className="text-slate-400 text-sm font-semibold">Enter your 2024/25 figures below to calculate your MTD start date.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">
              Annual Trading Income (Sales)
              <span className="ml-1 text-indigo-400 cursor-help" title="Use your 2024/25 figures">ⓘ</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">£</span>
              <input
                type="number"
                value={tradingIncome || ''}
                onChange={(e) => setTradingIncome(Number(e.target.value))}
                className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-8 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono"
                placeholder="0"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">
              Annual Property Rental Income
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">£</span>
              <input
                type="number"
                value={propertyIncome || ''}
                onChange={(e) => setPropertyIncome(Number(e.target.value))}
                className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-8 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div className={`p-6 rounded-2xl border transition-all duration-500 ${result.theme === 'red' ? 'bg-red-500/10 border-red-500/30' :
          result.theme === 'amber' ? 'bg-amber-500/10 border-amber-500/30' :
            result.theme === 'indigo' ? 'bg-indigo-500/10 border-indigo-500/30' :
              'bg-emerald-500/10 border-emerald-500/30'
          }`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded ${result.theme === 'red' ? 'bg-red-500 text-white' :
                result.theme === 'amber' ? 'bg-amber-500 text-black' :
                  result.theme === 'indigo' ? 'bg-indigo-500 text-white' :
                    'bg-emerald-500 text-white'
                }`}>
                {result.status}
              </span>
              <h4 className="text-3xl font-extrabold text-white mt-3">Starts {result.date}</h4>
              <p className="text-slate-300 mt-1">{result.message}</p>
            </div>
            <div className="text-center md:text-right">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-widest mb-1">Total Qualifying Income</p>
              <p className="text-2xl font-mono font-bold text-white">£{(tradingIncome + propertyIncome).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <a
            href="/#signup"
            className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-600/20 group"
          >
            <span>Start capturing receipts via WhatsApp</span>
            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>

        <div id="stat-mandation-leakage" className="mt-10 mb-2 bg-black/40 border border-slate-800 p-6 rounded-xl shadow-inner relative overflow-hidden">
          <p className="text-emerald-400 font-bold uppercase tracking-widest text-xs mb-1">The April 2027 £30k Threshold Drop</p>
          <p className="text-2xl font-extrabold text-white mb-2">1,580,000 Businesses Affected</p>
          <p className="text-slate-400 text-sm">
            "1.6 million individuals will be mandated for MTD by 2027; 780,000 in Phase 1 (£50k+) and 800,000 in Phase 2 (£30k+)."
          </p>
          <p className="mt-3 text-[10px] text-slate-500 italic border-t border-slate-800/50 pt-2">
            Source: <a href="https://www.gov.uk/government/publications/making-tax-digital-for-income-tax-self-assessment-reducing-the-mandation-threshold-from-30000-to-20000-from-april-2028" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">HMRC, MTD Threshold Reductions to April 2028</a>
          </p>
        </div>

        <div className="mt-6 mb-2 text-center">
          <a href={phase1.source} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-indigo-400 hover:text-indigo-300 underline">
            {phase1.sourceAlias} →
          </a>
        </div>


      </div>
    </div>
  );
};

export default MandationCalculator;
